/**
 * Library API Client
 *
 * Fetches user model library data from api-app service.
 * Uses apiCall helper for consistent authenticated requests.
 */

import type {
  LibraryEntryWithModel,
  RecentlyUsedEntry,
  LibraryEntry,
  BulkLibrary,
  BulkLibraryResult,
} from '@genieartinc/schemas';
import { apiCall } from '@/lib/api/api-helpers';

interface ApiData<T> {
  data: T;
}

export const libraryAPI = {
  /**
   * Get model IDs in the user's library — lightweight, for fast Set lookup.
   */
  async getLibraryModelIds(nodeType?: string): Promise<string[]> {
    const params = new URLSearchParams();
    if (nodeType) params.set('nodeType', nodeType);
    const endpoint = `/api/library/models/ids${params.toString() ? `?${params}` : ''}`;
    const result = await apiCall(endpoint, true) as ApiData<{ ids: string[] }>;
    return result.data.ids;
  },

  /**
   * Get the user's full library with model details.
   */
  async getLibrary(options?: {
    nodeType?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<LibraryEntryWithModel[]> {
    const params = new URLSearchParams();
    if (options?.nodeType) params.set('nodeType', options.nodeType);
    if (options?.search) params.set('search', options.search);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    const endpoint = `/api/library/models${params.toString() ? `?${params}` : ''}`;
    const result = await apiCall(endpoint, true) as ApiData<{ models: LibraryEntryWithModel[] }>;
    return result.data.models;
  },

  /**
   * Get recently used models.
   */
  async getRecentlyUsed(options?: { nodeType?: string; limit?: number }): Promise<RecentlyUsedEntry[]> {
    const params = new URLSearchParams();
    if (options?.nodeType) params.set('nodeType', options.nodeType);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    const endpoint = `/api/library/models/recent${params.toString() ? `?${params}` : ''}`;
    const result = await apiCall(endpoint, true) as ApiData<{ models: RecentlyUsedEntry[] }>;
    return result.data.models;
  },

  /**
   * Add a model to the library.
   */
  async addToLibrary(modelId: string, note?: string): Promise<LibraryEntry> {
    const result = await apiCall('/api/library/models', true, {
      method: 'POST',
      body: JSON.stringify({ modelId, note }),
    }) as ApiData<{ entry: LibraryEntry }>;
    return result.data.entry;
  },

  /**
   * Remove a model from the library.
   */
  async removeFromLibrary(modelId: string): Promise<void> {
    await apiCall(`/api/library/models/${modelId}`, true, {
      method: 'DELETE',
    });
  },

  /**
   * Add or remove many models: explicit ids, or every enabled model matching a catalog filter.
   * Returns the model IDs the action applied to.
   */
  async bulkUpdate(body: BulkLibrary): Promise<string[]> {
    const result = await apiCall('/api/library/models/bulk', true, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as ApiData<BulkLibraryResult>;
    return result.data.modelIds;
  },

  /**
   * Record model usage — fire-and-forget, never throws.
   */
  recordUsage(modelId: string, nodeType: string): void {
    apiCall('/api/library/models/usage', true, {
      method: 'POST',
      body: JSON.stringify({ modelId, nodeType }),
    }).catch(() => {
      // Fire-and-forget — ignore errors silently
    });
  },
};
